import { LineChartTraceData } from '../models/plots/linechart-trace-data.model';
import { LineChartTraceInput } from '../models/plots/linechart-trace-input.model';
import { PlotlyPlot } from '../models/plots/plotly-plot.model';

// lineInputs       - the data specific to each trace (line). It contains the trace unique x and y values along with other visual information.
// xLabels          - x values that are going to be set for each trace. This way multiple lines can be plotted for the same x positions but with different y values.
// xChartTitle      - the title shown at the bottom of the line chart explaining what the x axis mean.
// yChartTitle      - the title shown at the left of the line chart explaining what the y axis mean.
// xValueHoverLabel - title used in the hover template. When a user hovers over a point, information about that point is reveiled. Look at "hovertemplate" property defined below.
// yValueHoverLabel - title used in the hover template. When a user hovers over a point, information about that point is reveiled. Look at "hovertemplate" property defined below.
export class LineChartPlotly {
  public static create(
    lineInputs: LineChartTraceInput[],
    xLabels: string[],
    xChartTitle: string,
    yChartTitle: string,
    xValueHoverLabel: string,
    yValueHoverLabel: string,
  ): PlotlyPlot<LineChartTraceData> {
    const traces: LineChartTraceData[] = lineInputs.map((t) => ({
      x: xLabels,
      y: t.yValues,
      text: t.xValuesPeriod, // here we provide the texts for each x value that are going to be displayed in the hover. Used in the hover template.
      hovertemplate: `<b>${yValueHoverLabel}</b>: %{y}` + `<br><b>${xValueHoverLabel}</b>: %{text}<br>`,
      visible: t.disabled ? 'legendonly' : true,
      type: 'scatter',
      mode: 'lines+markers',
      name: '', // we do not provide a name here because it messes up with the hover template
      line: {
        color: t.color,
        width: 2,
      },
      marker: {
        size: 8,
        color: t.color,
      },
    }));

    const layout = {
      xaxis: {
        title: {
          text: xChartTitle,
        },
        type: 'category', // Forces the x values to NOT be interpolated. Only show the passed values, no between x values.
        fixedrange: true,
        automargin: true,
      },
      yaxis: {
        title: {
          text: yChartTitle,
        },
        fixedrange: true,
        automargin: true,
      },
      margin: {
        l: 40,
        r: 40,
        t: 40,
      },
      hovermode: 'x unified', // unified means that upon hovering a point it will show the info for the other points laying on the same x position. Good for comparing.
      legend: {
        visible: false,
      },
    };

    const linePlot: PlotlyPlot<LineChartTraceData> = {
      data: traces,
      layout: layout,
      config: {
        scrollZoom: false,
        doubleClick: false,
        displayModeBar: false,
        staticPlot: false,
      },
    };

    return linePlot;
  }
}
